'use strict';
const AmazonCognitoIdentity = require('amazon-cognito-identity-js');

module.exports = function CognitoIdentityShim(config) {
	if (!config || !config.UserPoolId || !config.ClientId || !config.cognitoIdentityPoolId || !config.cognitoIdentityPoolId.split) {
		throw 'invalid-args';
	}
	const self = this,
		poolData = {
			UserPoolId: config.UserPoolId,
			ClientId: config.ClientId
		},
		region = config.cognitoIdentityPoolId.split(':')[0],
		userPool = new AmazonCognitoIdentity.CognitoUserPool(poolData),
		getAuthenticationDetails = function (username, password) {
			const authenticationData = {
				Username: username,
				Password: password
			};
			return new AmazonCognitoIdentity.AuthenticationDetails(authenticationData);
		},
		buildCognitoUser = function (username) {
			const userData = {
				Username: username,
				Pool: userPool
			};
			return new AmazonCognitoIdentity.CognitoUser(userData);
		},
		knownErrors  = {
			UserNotFoundException: 'no-user'
		},
		parseError = function (cognitoError, contextSpecific) {
			const error = cognitoError && (cognitoError.name || cognitoError.code || cognitoError.message || cognitoError);
			return (contextSpecific && contextSpecific[error]) || knownErrors[error] || error;
		};

	self.cognitoUser = function (username) {
		return buildCognitoUser(username);
	};

	self.changePassword = function (oldPassword, newPassword) {
		return self.cognitoUserAndSession().then(function (userAndSession) {
			return new Promise(function (resolve, reject) {
				userAndSession.cognitoUser.changePassword(oldPassword, newPassword, function (err, result) {
					if (err) {
						reject(parseError(err));
					} else {
						resolve(result);
					}
				});
			});
		});
	};
	self.requestPasswordReset = function (username) {
		const cognitoUser = buildCognitoUser(username);
		return new Promise(function (resolve, reject) {
			const callbacks = {
				onSuccess: (result) => resolve({result: result}),
				onFailure: (reason) => reject(parseError(reason, {NotAuthorizedException: 'no-password'})),
				inputVerificationCode: (result) => resolve({result: result, action: 'completePendingUserAction', cognitoUser: cognitoUser})
			};
			cognitoUser.forgotPassword(callbacks);
		});

	};
	self.completePasswordReset = function (passwordResetUser, newPassword, verificationCode) {
		return new Promise(function (resolve, reject) {
			const callbacks = {
				onSuccess: function (result) {
					resolve(result);
				},
				onFailure: function (reason) {
					reject(parseError(reason));
				}
			};
			passwordResetUser.confirmPassword(verificationCode, newPassword, callbacks);
		});
	};
	self.setInitialPassword = function (username, newPassword, temporaryPassword) {
		const cognitoUser = buildCognitoUser(username),
			authenticationDetails = getAuthenticationDetails(username, temporaryPassword);
		return new Promise(function (resolve, reject) {
			cognitoUser.authenticateUser(authenticationDetails, {
				onSuccess: function () {
					resolve({cognitoUser: cognitoUser});
				},
				onFailure: function (err) {
					reject(parseError(err));
				},
				newPasswordRequired: function () {
					cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
						onSuccess: function () {
							resolve({cognitoUser: cognitoUser});
						},
						onFailure: function (err) {
							reject(parseError(err));
						}
					});
				}
			});
		});
	};
	self.authenticate = function (username, password) {
		const cognitoUser = buildCognitoUser(username),
			authenticationDetails = getAuthenticationDetails(username, password);
		return new Promise(function (resolve, reject) {
			cognitoUser.authenticateUser(authenticationDetails, {
				onSuccess: function () {
					resolve({cognitoUser: cognitoUser});
				},
				onFailure: function (err) {
					reject(parseError(err));
				},
				newPasswordRequired: function () {
					resolve({action: 'completeNewPasswordChallenge', user: cognitoUser});
				}
			});
		});
	};
	self.completeNewPasswordChallenge = (cognitoUser, newPassword) => {
		return new Promise((resolve, reject) => {
			cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
				onSuccess: function () {
					resolve({cognitoUser: cognitoUser});
				},
				onFailure: function (err) {
					reject(parseError(err));
				}
			});
		});
	};
	self.getIdentityToken = function (userSession) {
		const idToken = userSession && userSession.getIdToken && userSession.getIdToken();
		return idToken && idToken.getJwtToken && idToken.getJwtToken();
	};

	self.createIdentityCredentials = function (userSession) {
		if (!config || !config.UserPoolId || !config.cognitoIdentityPoolId) {
			throw 'invalid-args';
		}
		const idToken = userSession && userSession.getIdToken && userSession.getIdToken(),
			jwtToken = idToken && idToken.getJwtToken && idToken.getJwtToken(),
			creds = {
				IdentityPoolId: config.cognitoIdentityPoolId,
				Logins: { }
			};
		if (!jwtToken) {
			throw 'not-authenticated';
		}

		creds.Logins[`cognito-idp.${region}.amazonaws.com/${config.UserPoolId}`] = userSession.getIdToken().getJwtToken();
		return creds;
	};

	self.authenticateUsingCustomFlow = function (username) {
		const cognitoUser = buildCognitoUser(username),
			authenticationDetails = getAuthenticationDetails(username);
		return new Promise(function (resolve, reject) {
			cognitoUser.initiateAuth(authenticationDetails, {
				onSuccess: function () {
					resolve({cognitoUser: cognitoUser});
				},
				onFailure: function (err) {
					reject(parseError(err));
				},
				customChallenge: function (challengeParams) {
					resolve({action: 'customChallengeResponseRequired', result: challengeParams, cognitoUser: cognitoUser});
				}
			});
		});
	};
	self.respondToCustomChallenge = function (authResult, response) {
		const cognitoUser = authResult.cognitoUser;
		return new Promise((resolve, reject) => {
			cognitoUser.sendCustomChallengeAnswer(response, {
				onSuccess: function () {
					resolve({cognitoUser: cognitoUser});
				},
				onFailure: function (err) {
					reject(parseError(err));
				}
			});
		});
	};

	self.cognitoUserAndSession = function (optionalCognitoUser) {
		const cognitoUser = optionalCognitoUser || userPool.getCurrentUser();
		if (!cognitoUser) {
			return Promise.reject('not-authenticated');
		}
		return new Promise(function (resolve, reject) {
			cognitoUser.getSession(function (err, session) {
				if (err) {
					reject(parseError(err));
					return;
				} else {
					resolve({cognitoUser: cognitoUser, session: session});
				}
			});
		});
	};

	self.getUserAttributes = function (cognitoUser) {
		if (!cognitoUser) {
			return Promise.reject('invalid-args');
		}
		return new Promise(function (resolve, reject) {
			cognitoUser.getUserAttributes(function (err, result) {
				if (err) {
					reject(parseError(err));
				} else {
					const userAttributes = {};
					if (result) {
						for (let i = 0; i < result.length; i++) {
							userAttributes[result[i].getName()] = result[i].getValue();
						}
					}
					resolve(userAttributes);
				}
			});

		});

	};
};
